import { useState } from 'react';
import {
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  IconButton,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdCheckCircle,
  MdMoreVert,
  MdCalendarToday,
  MdAccessTime,
  MdPersonOff,
  MdCancel,
  MdAttachMoney,
  MdEditNote,
} from 'react-icons/md';
import { useSessionsByPlan } from 'hooks/useSessions';
import { EstadoSesion, Session, TipoSesion } from 'types/models';
import CompleteSessionModal from './CompleteSessionModal';
import SessionActionModal from './SessionActionModal';
import RegisterChargeModal from './RegisterChargeModal';

interface Props {
  patientId: string;
  episodeId: string;
  planId: string;
}

const ESTADO_BADGE: Record<EstadoSesion, { colorScheme: string; label: string }> = {
  [EstadoSesion.PROGRAMADA]: { colorScheme: 'blue',   label: 'Programada'  },
  [EstadoSesion.EN_CURSO]:   { colorScheme: 'orange', label: 'En curso'    },
  [EstadoSesion.ATENDIDA]:   { colorScheme: 'teal',   label: 'Atendida'    },
  [EstadoSesion.COMPLETADA]: { colorScheme: 'green',  label: 'Completada'  },
  [EstadoSesion.CANCELADA]:  { colorScheme: 'gray',   label: 'Cancelada'   },
  [EstadoSesion.NO_SHOW]:    { colorScheme: 'red',    label: 'No Asistió'  },
};

const TIPO_LABEL: Record<TipoSesion, string> = {
  [TipoSesion.FISIOTERAPIA]:      'Fisioterapia',
  [TipoSesion.EVALUACION_FISICA]: 'Evaluación física',
  [TipoSesion.INTERCONSULTA]:     'Interconsulta',
  [TipoSesion.CONSULTA_MEDICA]:   'Consulta médica',
};

function formatFecha(d: string) {
  return new Date(d).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
}

function sessionBorderColor(estado: EstadoSesion, defaultClr: string): string {
  if (estado === EstadoSesion.COMPLETADA) return 'green.200';
  if (estado === EstadoSesion.ATENDIDA)   return 'teal.200';
  if (estado === EstadoSesion.NO_SHOW)    return 'red.200';
  if (estado === EstadoSesion.CANCELADA)  return 'gray.300';
  return defaultClr;
}

function sessionBorderHover(estado: EstadoSesion): string {
  if (estado === EstadoSesion.COMPLETADA) return 'green.300';
  if (estado === EstadoSesion.ATENDIDA)   return 'teal.300';
  if (estado === EstadoSesion.NO_SHOW)    return 'red.300';
  if (estado === EstadoSesion.CANCELADA)  return 'gray.400';
  return 'brand.200';
}

interface SessionRowProps {
  session: Session;
  patientId: string;
  episodeId: string;
  planId: string;
}

function SessionRow({ session, patientId, episodeId, planId }: SessionRowProps) {
  const [registerOpen, setRegisterOpen]   = useState(false);
  const [completeOpen, setCompleteOpen]   = useState(false);
  const [noShowOpen, setNoShowOpen]       = useState(false);
  const [cancelOpen, setCancelOpen]       = useState(false);

  const rowBg      = useColorModeValue('white',   'gray.800');
  const borderClr  = useColorModeValue('gray.200', 'gray.700');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const numBg      = useColorModeValue('brand.50', 'rgba(99,102,241,0.15)');

  const cfg         = ESTADO_BADGE[session.estado] ?? { colorScheme: 'gray', label: session.estado };
  const isScheduled = session.estado === EstadoSesion.PROGRAMADA;
  const isAtendida  = session.estado === EstadoSesion.ATENDIDA;
  const isCompleted = session.estado === EstadoSesion.COMPLETADA;
  const isNoShow    = session.estado === EstadoSesion.NO_SHOW;
  const isCancelled = session.estado === EstadoSesion.CANCELADA;
  const isTerminal  = isCompleted || isNoShow || isCancelled;

  return (
    <>
      <Flex
        align="center"
        px="14px" py="10px"
        bg={rowBg}
        border="1px solid"
        borderColor={sessionBorderColor(session.estado, borderClr)}
        borderRadius="10px"
        gap="12px"
        transition="border-color 0.15s"
        opacity={isTerminal && !isCompleted && !isAtendida ? 0.75 : 1}
        _hover={{ borderColor: sessionBorderHover(session.estado) }}
      >
        {/* Session number / icon */}
        <Flex
          w="32px" h="32px" borderRadius="8px"
          bg={
            isCompleted ? 'green.50'
            : isAtendida  ? 'teal.50'
            : isNoShow    ? 'red.50'
            : isCancelled ? 'gray.100'
            : numBg
          }
          align="center" justify="center" flexShrink={0}
        >
          {isCompleted ? (
            <Icon as={MdCheckCircle} color="green.500" w="16px" h="16px" />
          ) : isAtendida ? (
            <Icon as={MdAttachMoney} color="teal.500" w="16px" h="16px" />
          ) : isNoShow ? (
            <Icon as={MdPersonOff} color="red.400" w="16px" h="16px" />
          ) : isCancelled ? (
            <Icon as={MdCancel} color="gray.400" w="16px" h="16px" />
          ) : (
            <Text fontSize="xs" fontWeight="800" color="brand.500">{session.numeroSesion}</Text>
          )}
        </Flex>

        {/* Meta */}
        <Box flex="1" minW="0">
          <HStack spacing="8px" mb="2px" flexWrap="wrap">
            <Badge
              colorScheme={cfg.colorScheme}
              borderRadius="full" px="8px" py="1px" fontSize="xs" fontWeight="700"
            >
              {cfg.label}
            </Badge>
            <Text fontSize="xs" color={labelColor}>{TIPO_LABEL[session.tipo] ?? session.tipo}</Text>
          </HStack>
          <HStack spacing="12px" color={labelColor}>
            <HStack spacing="4px">
              <Icon as={MdCalendarToday} w="11px" h="11px" />
              <Text fontSize="xs">{formatFecha(session.fechaSesion)}</Text>
            </HStack>
            <HStack spacing="4px">
              <Icon as={MdAccessTime} w="11px" h="11px" />
              <Text fontSize="xs">Sesión #{session.numeroSesion}</Text>
            </HStack>
            {session.motivo && (
              <Text fontSize="xs" color={labelColor} noOfLines={1} maxW="180px" fontStyle="italic">
                {session.motivo}
              </Text>
            )}
          </HStack>
        </Box>

        {/* Actions — PROGRAMADA */}
        {isScheduled && (
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              icon={<Icon as={MdMoreVert} />}
              variant="ghost"
              size="sm"
              borderRadius="8px"
              aria-label="Acciones de sesión"
              flexShrink={0}
            />
            <MenuList fontSize="sm" minW="210px">
              <MenuItem
                icon={<Icon as={MdAttachMoney} color="teal.500" />}
                onClick={() => setRegisterOpen(true)}
                fontWeight="600"
                color="teal.600"
              >
                Registrar Atención
              </MenuItem>
              <MenuDivider />
              <MenuItem
                icon={<Icon as={MdPersonOff} color="red.400" />}
                onClick={() => setNoShowOpen(true)}
                fontWeight="600"
                color="red.500"
              >
                Marcar Inasistencia
              </MenuItem>
              <MenuItem
                icon={<Icon as={MdCancel} color="gray.400" />}
                onClick={() => setCancelOpen(true)}
                fontWeight="600"
                color="gray.600"
              >
                Cancelar Sesión
              </MenuItem>
            </MenuList>
          </Menu>
        )}

        {/* Actions — ATENDIDA (cobro listo, SOAP pendiente) */}
        {isAtendida && (
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              icon={<Icon as={MdMoreVert} />}
              variant="ghost"
              size="sm"
              borderRadius="8px"
              aria-label="Acciones de sesión"
              flexShrink={0}
            />
            <MenuList fontSize="sm" minW="210px">
              <MenuItem
                icon={<Icon as={MdEditNote} color="green.500" />}
                onClick={() => setCompleteOpen(true)}
                fontWeight="600"
                color="green.600"
              >
                Llenar nota SOAP
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </Flex>

      {registerOpen && (
        <RegisterChargeModal
          isOpen={registerOpen}
          onClose={() => setRegisterOpen(false)}
          session={session}
          patientId={patientId}
          episodeId={episodeId}
          planId={planId}
        />
      )}

      {completeOpen && (
        <CompleteSessionModal
          isOpen={completeOpen}
          onClose={() => setCompleteOpen(false)}
          session={session}
          patientId={patientId}
          episodeId={episodeId}
          planId={planId}
        />
      )}

      {noShowOpen && (
        <SessionActionModal
          isOpen={noShowOpen}
          onClose={() => setNoShowOpen(false)}
          actionType="no-show"
          session={session}
          patientId={patientId}
          episodeId={episodeId}
          planId={planId}
        />
      )}

      {cancelOpen && (
        <SessionActionModal
          isOpen={cancelOpen}
          onClose={() => setCancelOpen(false)}
          actionType="cancel"
          session={session}
          patientId={patientId}
          episodeId={episodeId}
          planId={planId}
        />
      )}
    </>
  );
}

export default function SessionsPanel({ patientId, episodeId, planId }: Props) {
  const { data: sessions, isLoading } = useSessionsByPlan(patientId, episodeId, planId);

  const emptyBg     = useColorModeValue('gray.50', 'gray.750');
  const emptyBorder = useColorModeValue('gray.200', 'gray.600');
  const labelColor  = useColorModeValue('gray.400', 'gray.500');

  if (isLoading) {
    return (
      <Flex align="center" justify="center" py="40px">
        <Spinner color="brand.500" />
      </Flex>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <Flex
        direction="column" align="center" justify="center" flex="1"
        bg={emptyBg} borderRadius="12px"
        border="2px dashed" borderColor={emptyBorder}
        gap="10px" py="48px"
      >
        <Icon as={MdCalendarToday} color={labelColor} w="28px" h="28px" />
        <Text fontSize="sm" color={labelColor} textAlign="center" maxW="260px">
          Sin sesiones programadas en este plan aún
        </Text>
      </Flex>
    );
  }

  const completed = sessions.filter(s => s.estado === EstadoSesion.COMPLETADA).length;
  const attended  = sessions.filter(s => s.estado === EstadoSesion.ATENDIDA).length;
  const noShows   = sessions.filter(s => s.estado === EstadoSesion.NO_SHOW).length;
  const total     = sessions.length;

  return (
    <Flex direction="column" h="100%" gap="12px">
      <HStack justify="space-between" mb="2px">
        <Text fontSize="xs" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em">
          Sesiones del plan
        </Text>
        <HStack spacing="6px">
          {attended > 0 && (
            <Badge colorScheme="teal" variant="subtle" borderRadius="full" px="8px" fontSize="xs">
              {attended} SOAP pendiente{attended > 1 ? 's' : ''}
            </Badge>
          )}
          {noShows > 0 && (
            <Badge colorScheme="red" variant="subtle" borderRadius="full" px="8px" fontSize="xs">
              {noShows} inasistencia{noShows > 1 ? 's' : ''}
            </Badge>
          )}
          <Badge colorScheme="green" variant="subtle" borderRadius="full" px="8px" fontSize="xs">
            {completed}/{total} completadas
          </Badge>
        </HStack>
      </HStack>

      <VStack spacing="8px" align="stretch" flex="1" overflowY="auto">
        {sessions.map(session => (
          <SessionRow
            key={session.id}
            session={session}
            patientId={patientId}
            episodeId={episodeId}
            planId={planId}
          />
        ))}
      </VStack>
    </Flex>
  );
}
